import { useState } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Lock, Mail, User, Building, CheckCircle, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DefendlyLogo, BSECBadge } from '@/components/DefendlyLogo';

export default function Login({ onLogin, defaultToRegister = false }) {
  const [isRegister, setIsRegister] = useState(defaultToRegister);
  const [showThankYou, setShowThankYou] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    organization_name: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRegister) {
        // For registration, show thank you page instead of actual registration
        await api.post('/auth/register-interest', formData);
        setShowThankYou(true);
        setLoading(false);
        return;
      }
      
      const endpoint = '/auth/login';
      const payload = { email: formData.email, password: formData.password };

      const response = await api.post(endpoint, payload);
      const { access_token, user } = response.data;

      onLogin(access_token, user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  // Thank You Screen after registration
  if (showThankYou) {
    return (
      <div className="min-h-screen bg-navy-950 relative overflow-hidden flex items-center justify-center">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-navy-950 via-[#0A1429] to-navy-900" />
          <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-electric-500/8 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-0 w-[350px] h-[350px] bg-teal-500/6 rounded-full blur-[80px]" />
          <div 
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, rgba(0, 191, 255, 0.12) 1px, transparent 0)`,
              backgroundSize: '48px 48px'
            }}
          />
        </div>

        <div className="relative z-10 w-full max-w-md px-8">
          <div className="bg-navy-800/70 backdrop-blur-xl border border-navy-700 rounded-2xl p-8 text-center shadow-glass-dark">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-500/15 border border-emerald-500/30 rounded-full mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-400" style={{ filter: 'drop-shadow(0 0 8px rgba(52, 211, 153, 0.6))' }} />
            </div>
            
            <h1 className="font-inter font-bold text-3xl text-white mb-4">
              Thank You for Registering!
            </h1>
            
            <p className="text-slate-400 font-inter mb-6">
              Our Sales team will get in touch with you shortly to provide more information about Defendly AI and help you get started.
            </p>
            
            <div className="bg-navy-900/60 border border-navy-700 p-4 rounded-xl mb-6 text-left">
              <p className="text-slate-500 text-xs mb-1 font-inter">Registered Email</p>
              <p className="text-white font-mono text-sm">{formData.email}</p>
              {formData.organization_name && (
                <>
                  <p className="text-slate-500 text-xs mt-3 mb-1 font-inter">Organization</p>
                  <p className="text-white font-mono text-sm">{formData.organization_name}</p>
                </>
              )}
            </div>
            
            <Button
              onClick={() => {
                setShowThankYou(false);
                setIsRegister(false);
                setFormData({ email: '', password: '', full_name: '', organization_name: '' });
              }}
              className="w-full bg-electric-500 hover:bg-electric-600 text-navy-950 font-inter font-semibold"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Button>
          </div>
          
          <div className="mt-6 text-center text-xs text-slate-500 font-inter">
            Our team typically responds within 24 business hours
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-950 relative overflow-hidden flex">
      {/* Left Side - Hero Image */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-navy-950 z-10"></div>
        <img 
          src="https://static.prod-images.emergentagent.com/jobs/19184fab-55e7-464f-8bdb-3d08b92eeb52/images/841c3a2bd5e24299a86ca00da048ea72ad1b0493919db02c0eab2c66e4ef2764.png"
          alt="Defendly Cyber Security Command Center"
          className="w-full h-full object-cover"
        />
        {/* Overlay Content */}
        <div className="absolute bottom-0 left-0 right-0 p-12 z-20 bg-gradient-to-t from-navy-950 via-navy-950/80 to-transparent">
          <h2 className="font-inter font-bold text-4xl text-white mb-4" style={{ textShadow: '0 0 20px rgba(0, 191, 255, 0.4)' }}>
            Continuous Threat &<br />Exposure Management
          </h2>
          <p className="text-slate-400 font-inter max-w-md">
            AI-powered vulnerability management, threat intelligence, and attack surface monitoring in one unified platform.
          </p>
          <div className="flex items-center gap-6 mt-6 text-xs font-mono text-slate-500">
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
              REAL-TIME MONITORING
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-electric-400 rounded-full animate-pulse"></span>
              AI-POWERED INSIGHTS
            </span>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 relative">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-navy-950 via-[#0A1429] to-navy-900" />
          <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-electric-500/8 rounded-full blur-[100px] animate-pulse-glow" />
          <div className="absolute bottom-0 left-0 w-[350px] h-[350px] bg-teal-500/6 rounded-full blur-[80px] animate-pulse-glow" style={{ animationDelay: '2s' }} />
          
          {/* Network mesh pattern */}
          <div 
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, rgba(0, 191, 255, 0.15) 1px, transparent 0)`,
              backgroundSize: '48px 48px'
            }}
          />
          
          {/* Animated Particles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(20)].map((_, i) => (
              <div 
                key={i}
                className="absolute rounded-full"
                style={{
                  width: `${2 + Math.random() * 3}px`,
                  height: `${2 + Math.random() * 3}px`,
                  background: `rgba(0, 191, 255, ${0.3 + Math.random() * 0.4})`,
                  boxShadow: '0 0 6px rgba(0, 191, 255, 0.4)',
                  left: `${Math.random() * 100}%`,
                  top: `${100 + Math.random() * 20}%`,
                  animation: `particle-float ${12 + Math.random() * 8}s linear infinite`,
                  animationDelay: `${Math.random() * 12}s`
                }}
              />
            ))}
          </div>
          
          {/* Floating connection lines */}
          <svg className="absolute inset-0 w-full h-full opacity-10 pointer-events-none">
            <defs>
              <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#00BFFF" stopOpacity="0" />
                <stop offset="50%" stopColor="#00BFFF" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#00BFFF" stopOpacity="0" />
              </linearGradient>
            </defs>
            {[...Array(5)].map((_, i) => (
              <line
                key={i}
                x1={`${10 + i * 20}%`}
                y1="0%"
                x2={`${30 + i * 15}%`}
                y2="100%"
                stroke="url(#lineGradient)"
                strokeWidth="1"
                className="animate-network"
                style={{ animationDelay: `${i * 1.5}s` }}
              />
            ))}
          </svg>
        </div>

        <div className="relative z-10 w-full max-w-md">
          <div className="text-center mb-8">
            {/* New Defendly AI Logo */}
            <div className="flex justify-center mb-4">
              <DefendlyLogo size="xl" showText={false} />
            </div>
            <h1 className="font-inter font-bold text-4xl text-white mb-2">
              Defendly<span className="text-slate-400">AI</span>
            </h1>
            <div className="flex items-center justify-center gap-2 mt-2">
              <BSECBadge size="lg" />
              <span className="font-inter text-slate-400 text-sm">
                Business Security Exposure Center
              </span>
            </div>
          </div>

          <div className="bg-navy-800/70 backdrop-blur-xl border border-navy-700 rounded-2xl p-6 sm:p-8 shadow-glass-dark">
            <div className="flex gap-2 mb-6">
              <button
                data-testid="login-tab"
                onClick={() => setIsRegister(false)}
                className={`flex-1 py-2 rounded-lg font-inter font-semibold transition-all ${
                  !isRegister
                    ? 'bg-electric-500 text-navy-950 shadow-neon-electric'
                    : 'bg-white/5 text-slate-400 hover:bg-white/10'
                }`}
              >
                Login
              </button>
              <button
                data-testid="register-tab"
                onClick={() => setIsRegister(true)}
                className={`flex-1 py-2 rounded-lg font-inter font-semibold transition-all ${
                  isRegister
                    ? 'bg-electric-500 text-navy-950 shadow-neon-electric'
                    : 'bg-white/5 text-slate-400 hover:bg-white/10'
                }`}
              >
                Register
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {isRegister && (
                <>
                  <div>
                    <Label htmlFor="full_name" className="text-zinc-300 font-inter text-sm mb-2 block">
                      Full Name
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        id="full_name"
                        data-testid="full-name-input"
                        type="text"
                        placeholder="John Doe"
                        value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        className="pl-10 bg-zinc-950/50 border-white/10 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 text-white"
                        required={isRegister}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="organization_name" className="text-zinc-300 font-inter text-sm mb-2 block">
                      Organization Name
                    </Label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        id="organization_name"
                        data-testid="organization-name-input"
                        type="text"
                        placeholder="Acme Corp"
                        value={formData.organization_name}
                        onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                        className="pl-10 bg-zinc-950/50 border-white/10 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 text-white"
                      />
                    </div>
                  </div>
                </>
              )}

              <div>
                <Label htmlFor="email" className="text-zinc-300 font-inter text-sm mb-2 block">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 bg-zinc-950/50 border-white/10 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 text-white"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-zinc-300 font-inter text-sm mb-2 block">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="password"
                    data-testid="password-input"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 pr-10 bg-zinc-950/50 border-white/10 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 text-white"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                data-testid="auth-submit-button"
                type="submit"
                disabled={loading}
                className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-rajdhani font-semibold tracking-wide py-6 rounded-lg shadow-neon-indigo hover:shadow-neon-indigo-lg transition-all"
              >
                {loading ? 'Processing...' : isRegister ? 'Create Account' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-zinc-500 text-sm font-inter">
                {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
                <button
                  onClick={() => setIsRegister(!isRegister)}
                  className="text-indigo-400 hover:text-indigo-300 font-medium transition-colors"
                >
                  {isRegister ? 'Sign in' : 'Register now'}
                </button>
              </p>
            </div>
          </div>

          <div className="mt-6 text-center text-xs text-zinc-600 font-inter">
            Continuous Threat and Exposure Management Platform
          </div>
        </div>
      </div>
    </div>
  );
}
